import React, { useState, useEffect, useCallback } from 'react';

const StatusDashboard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('https://ourzestapp.online/oz_backend/status.php', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setStatus(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Status fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStatus, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchStatus]);

  const getHealthColor = (health) => {
    switch(health) {
      case 'healthy': return '#4CAF50';
      case 'unhealthy': return '#f44336';
      case 'disconnected': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getEfficiencyColor = (efficiency) => {
    switch(efficiency) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#2196F3';
      case 'warning': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading && !status) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading system status...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔧 System Health Monitor</h1>
        <div style={styles.headerControls}>
          <button 
            onClick={() => fetchStatus()} 
            style={styles.refreshButton}
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <label style={styles.autoRefreshLabel}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={styles.autoRefreshCheckbox}
            />
            Auto-refresh (30s)
          </label>
        </div>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          ⚠️ Error fetching status: {error}
        </div>
      )}

      {status && (
        <>
          {/* Alerts */}
          {status.alerts && status.alerts.length > 0 && (
            <div style={styles.alertsContainer}>
              {status.alerts.map((alert, index) => (
                <div key={index} style={styles.alert}>
                  ⚠️ {alert}
                </div>
              ))}
            </div>
          )}

          {/* Database Status */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>🗄️ Database Status</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Health</span>
                <span style={{
                  ...styles.statValue,
                  color: getHealthColor(status.database?.health)
                }}>
                  {status.database?.health || 'Unknown'}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Active Connections</span>
                <span style={styles.statValue}>
                  {status.database?.active_connections || 0}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Managed Connections</span>
                <span style={styles.statValue}>
                  {status.database?.managed_connections || 0}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Max Used Connections</span>
                <span style={styles.statValue}>
                  {status.database?.max_used_connections || 0}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Database Size</span>
                <span style={styles.statValue}>
                  {status.database?.size_mb || 0} MB
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Slow Queries</span>
                <span style={{
                  ...styles.statValue,
                  color: (status.database?.slow_queries || 0) > 100 ? '#f44336' : '#4CAF50'
                }}>
                  {status.database?.slow_queries || 0}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Total Queries</span>
                <span style={styles.statValue}>
                  {(status.database?.total_queries || 0).toLocaleString()}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Uptime</span>
                <span style={styles.statValue}>
                  {status.database?.uptime_seconds 
                    ? Math.floor(status.database.uptime_seconds / 3600) + 'h ' + 
                      Math.floor((status.database.uptime_seconds % 3600) / 60) + 'm'
                    : 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>⚡ Performance Metrics</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Response Time</span>
                <span style={{
                  ...styles.statValue,
                  color: (status.performance?.response_time_ms || 9999) < 500 ? '#4CAF50' : 
                         (status.performance?.response_time_ms || 9999) < 1000 ? '#ff9800' : '#f44336'
                }}>
                  {status.performance?.response_time_ms || 0} ms
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Query Cache Hit Rate</span>
                <span style={styles.statValue}>
                  {status.performance?.query_cache_hit_rate || 0}%
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Connection Efficiency</span>
                <span style={{
                  ...styles.statValue,
                  color: getEfficiencyColor(status.performance?.connection_efficiency)
                }}>
                  {status.performance?.connection_efficiency || 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          {/* Server Status */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>🖥️ Server Status</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>PHP Version</span>
                <span style={styles.statValue}>{status.server?.php_version || 'Unknown'}</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Memory Usage</span>
                <span style={styles.statValue}>
                  {status.server?.memory_usage ? formatBytes(status.server.memory_usage) : 'Unknown'}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Memory Peak</span>
                <span style={styles.statValue}>
                  {status.server?.memory_peak ? formatBytes(status.server.memory_peak) : 'Unknown'}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Load Average</span>
                <span style={{
                  ...styles.statValue,
                  color: (status.server?.load_average?.[0] || 0) > 5 ? '#f44336' : '#4CAF50'
                }}>
                  {status.server?.load_average?.map(l => l.toFixed(2)).join(', ') || 'Unknown'}
                </span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Server Uptime</span>
                <span style={styles.statValue}>{status.server?.uptime || 'Unknown'}</span>
              </div>
              <div style={styles.statItem}>
                <span style={styles.statLabel}>Server Software</span>
                <span style={styles.statValue}>{status.server?.server_software || 'Unknown'}</span>
              </div>
            </div>
          </div>

          {/* Last Update */}
          <div style={styles.footer}>
            <span>Last updated: {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}</span>
            <span style={styles.statusIndicator}>
              {autoRefresh ? '🟢 Auto-refresh enabled' : '⚪ Auto-refresh disabled'}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// Internal CSS Styles
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '28px',
    color: '#333',
    margin: 0
  },
  headerControls: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center'
  },
  refreshButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.3s'
  },
  autoRefreshLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#666'
  },
  autoRefreshCheckbox: {
    cursor: 'pointer'
  },
  errorBanner: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    borderLeft: '4px solid #c62828'
  },
  alertsContainer: {
    marginBottom: '20px'
  },
  alert: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    padding: '10px 15px',
    borderRadius: '8px',
    marginBottom: '10px',
    borderLeft: '4px solid #ff9800'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '20px',
    color: '#333',
    marginTop: 0,
    marginBottom: '20px',
    borderBottom: '2px solid #e0e0e0',
    paddingBottom: '10px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: 'white',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#666'
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  }
};

// Add keyframe animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default StatusDashboard;